import { useState } from "react";
import { QrCode, Star, RefreshCw, MessageCircle, ChevronRight, ChevronLeft, X } from "lucide-react";
import { useDashboardI18n } from "../lib/useDashboardI18n";

const CARD_KEYS = [
  { icon: QrCode,        titleKey: "ven_onb_card1_title", bodyKey: "ven_onb_card1_body" },
  { icon: Star,          titleKey: "ven_onb_card2_title", bodyKey: "ven_onb_card2_body" },
  { icon: RefreshCw,     titleKey: "ven_onb_card3_title", bodyKey: "ven_onb_card3_body" },
  { icon: MessageCircle, titleKey: "ven_onb_card4_title", bodyKey: "ven_onb_card4_body" },
];

export function VendorOnboarding({ onClose }) {
  const { t, lang, setLang } = useDashboardI18n("vendor");
  const [i, setI] = useState(0);
  const C = CARD_KEYS[i];
  const Icon = C.icon;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-6 py-8"
         style={{ background: "rgba(11,11,11,0.92)", backdropFilter: "blur(8px)" }}
         data-testid="vendor-onboarding">
      <div
        className="max-w-md w-full p-6 relative"
        style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 16 }}
      >
        <button
          onClick={() => onClose(false)}
          data-testid="onboarding-close"
          className="absolute top-3 right-3 p-2 text-foreground/45 hover:text-foreground"
          style={{ minHeight: 48, minWidth: 48 }}
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={1.5}/>
        </button>

        {/* EN/ES toggle — top-left, matches dashboard header style */}
        <div
          style={{
            position: "absolute", top: 14, left: 14,
            display: "inline-flex", border: "1px solid rgba(232,184,75,0.4)",
            borderRadius: 999, padding: 2,
          }}
        >
          {["en", "es"].map((code) => (
            <button
              key={code}
              type="button"
              data-testid={`onboarding-lang-${code}`}
              onClick={() => setLang(code)}
              style={{
                padding: "4px 10px", minHeight: 28, borderRadius: 999, fontSize: 11,
                background: lang === code ? "#E8B84B" : "transparent",
                color: lang === code ? "#0B0B0B" : "#E8B84B",
                textTransform: "uppercase", letterSpacing: 1, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >{code}</button>
          ))}
        </div>

        <div className="eyebrow text-center pt-6">
          {t("ven_onb_eyebrow", { n: i + 1, total: CARD_KEYS.length })}
        </div>

        <div className="mt-6 grid place-items-center">
          <span className="w-16 h-16 grid place-items-center rounded-full border border-gold-strong text-gold bg-gold/10">
            <Icon className="w-7 h-7" strokeWidth={1.5} />
          </span>
        </div>
        <h2 className="font-display text-3xl text-center mt-5 text-foreground" data-testid={`onboarding-title-${i}`}>
          {t(C.titleKey)}
        </h2>
        <p className="text-base text-foreground/65 text-center mt-3 leading-relaxed"
           style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}>
          {t(C.bodyKey)}
        </p>

        <div className="flex justify-center gap-1.5 mt-6">
          {CARD_KEYS.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} data-testid={`onboarding-dot-${idx}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "bg-gold w-6" : "bg-foreground/20 w-1.5"}`}
              style={{ minHeight: 48 }}
              aria-label={`Card ${idx + 1}`} />
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          {i > 0 && (
            <button onClick={() => setI(i - 1)} data-testid="onboarding-prev"
              className="btn-outline py-2 px-3 text-xs inline-flex items-center gap-1"
              style={{ minHeight: 48 }}>
              <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.5}/> {t("ven_onb_back")}
            </button>
          )}
          <div className="flex-1" />
          {i < CARD_KEYS.length - 1 ? (
            <button onClick={() => setI(i + 1)} data-testid="onboarding-next"
              className="btn-gold py-2 px-4 text-xs inline-flex items-center gap-1"
              style={{ minHeight: 48 }}>
              {t("ven_onb_next")} <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
          ) : (
            <button onClick={() => onClose(true)} data-testid="onboarding-done"
              className="btn-gold py-2 px-4 text-xs"
              style={{ minHeight: 48 }}>
              {t("ven_onb_done")}
            </button>
          )}
        </div>

        <button onClick={() => onClose(false)} data-testid="onboarding-skip"
          className="block w-full text-center text-[11px] text-foreground/45 mt-4 hover:text-foreground/65"
          style={{ minHeight: 48 }}>
          {t("ven_onb_skip")}
        </button>
      </div>
    </div>
  );
}
